import { Button, Text } from 'Common';
import CONFIG from 'Config/settings';
import DynamicFormContainerModal from 'ExtendedForm/DynamicForm/DynamicFormContainerModal';
import { PropTypes } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Redirect } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { getDeliveryAddresses } from 'Redux/addresses/addressesThunks';
import { hasValidSession, login } from 'Redux/auth/authThunks';
import {
  getOrderHistoryCount,
  getShoppingListCount
} from 'Redux/commerce/commerceThunks';
import { clearFormAction } from 'Redux/forms/formsActions';
import { getPayments } from 'Redux/payment/paymentThunks';
import { PROFILE_TYPES } from 'Redux/profile/profileConstants';
import { getProfile, upgradeProfile } from 'Redux/profile/profileThunks';
import { LOADING } from 'Redux/userInterface/userInterfaceConstants';
import Routes, { QUERY_PARAMS } from 'Routes/profileRoutes';
import { appendAuthTokenToUrl } from 'Utils/auth/authUtils';
import { setPageTitle } from 'Utils/document/documentUtils';
import { t } from 'Utils/localization/i18next';
import { showToast } from 'Utils/toastUtils';
import { getQueryStringValue, isValidIKEAUrl } from 'Utils/url/urlUtils';
import styles from './UpgradeToFamilyCard.module.scss';

class UpgradeToFamilyCard extends React.PureComponent {
  constructor(props) {
    super(props);
    this.upgradeFormId = 'regular-upgrade-form';

    const { search } = props.location;
    const showUpgrade = getQueryStringValue(search, QUERY_PARAMS.SHOW_UPGRADE);
    this.redirectToAfterUpgrade = getQueryStringValue(
      search,
      QUERY_PARAMS.REDIRECT_TO_AFTER_UPGRADE
    );

    this.state = {
      isEditing: false,
      showUpgrade: showUpgrade === 'true',
      isConfirming: false,
      modalOpen: false
    };
    this.myRef = React.createRef();

    this.clearSearchParams();
  }

  componentDidMount() {
    setPageTitle(t(['metaData.upgradePageTitle', 'metaData.defaultPageTitle']));
    this.init();
  }

  componentWillUnmount() {
    this.props.clearFormAction(this.upgradeFormId);
  }

  clearSearchParams = () => {
    // Strip upgrade-related query params from the URL without a page reload
    const cleanParams = new URLSearchParams(this.props.location.search);
    cleanParams.delete(QUERY_PARAMS.SHOW_UPGRADE);
    cleanParams.delete(QUERY_PARAMS.REDIRECT_TO_AFTER_UPGRADE);
    const cleanSearch = cleanParams.toString();
    this.props.history.replace({
      ...this.props.location,
      search: cleanSearch ? `?${cleanSearch}` : ''
    });
  };

  init = async () => {
    const isLoggedIn = await this.props.hasValidSession();

    if (!isLoggedIn) {
      this.props.login();
    } else {
      this.getDashboardData();
    }
  };

  getDashboardData = async () => {
    if (!this.props.profileFetched) {
      await this.props.getProfile();
      this.props.getDeliveryAddresses();

      const isRegular = this.props.profileType === PROFILE_TYPES.REGULAR;
      const isFamily = this.props.profileType === PROFILE_TYPES.FAMILY;

      if (
        (CONFIG.FEATURE.REGULAR_LINKBOXES.ENABLE_ORDER_HISTORY && isRegular) ||
        (CONFIG.FEATURE.FAMILY_LINKBOXES.ENABLE_ORDER_HISTORY && isFamily)
      ) {
        this.props.getOrderHistoryCount();
      }

      if (
        (CONFIG.FEATURE.REGULAR_LINKBOXES.ENABLE_SHOPPING_LISTS && isRegular) ||
        (CONFIG.FEATURE.FAMILY_LINKBOXES.ENABLE_SHOPPING_LISTS && isFamily)
      ) {
        this.props.getShoppingListCount();
      }

      if (
        CONFIG.FEATURE.REGULAR_DASHBOARD.ENABLE_PAYMENTS_TAB ||
        CONFIG.FEATURE.FAMILY_DASHBOARD.ENABLE_PAYMENTS_TAB
      )
        await this.props.getPayments();
    }
  };

  onSubmit = async data => {
    const success = await this.props.upgradeProfile(
      { ...this.props.profile, ...data },
      this.upgradeFormId,
      this.redirectToAfterUpgrade
    );
    if (success) {
      this.setState({ isEditing: false });
      showToast(
        t('dashboard.confirmation.addressPrimarySectionUpdateTitle'),
        t('dashboard.confirmation.addressPrimarySectionUpdateText')
      );
    }
  };

  onClose = () => {
    this.setState({ isEditing: false, showUpgrade: false });
  };

  onEdit = () => {
    this.setState({ isEditing: true });
  };

  render() {
    if (this.props.profileType === PROFILE_TYPES.FAMILY) {
      if (isValidIKEAUrl(this.from)) {
        // User is already logged in as Family user and
        // upgrade should return to a different URL "from".
        window.location = appendAuthTokenToUrl(this.from);
        return null;
      }
      // User is already logged in as Family user.
      return <Redirect to={Routes.ROOT} />;
    }

    return (
      <>
        <div className={styles['upgrade-to-family-card']}>
          <div>
            <Text className={styles['banner-title']}>
              {t('forms.signup.pageTitleUpgrade')}
            </Text>
            <Text className={styles['body-style']}>
              {t('dashboard.upgrade.body')}
            </Text>
          </div>
          <div>
            <Button
              type="primary"
              onClick={this.onEdit}
              id="link-upgrade-to-family"
              inverseTheme
            >
              {t('dashboard.upgrade.linkText')}
            </Button>
          </div>
        </div>
        {(this.state.isEditing || this.state.showUpgrade) && (
          <DynamicFormContainerModal
            formId={this.upgradeFormId}
            prefillValues={this.props.profile}
            loadingSubmit={this.props.isLoading}
            textSubmit={t('forms.signup.btnSubmit')}
            textCancel={t('common.backToAccount')}
            onSubmit={this.onSubmit}
            onCancel={this.onClose}
          />
        )}
      </>
    );
  }
}

UpgradeToFamilyCard.propTypes = {
  isLoading: PropTypes.bool,
  upgradeFormId: PropTypes.string,
  profile: PropTypes.object,
  profileType: PropTypes.string,
  profileFetched: PropTypes.bool.isRequired,
  location: PropTypes.object,
  history: PropTypes.object,
  getProfile: PropTypes.func.isRequired,
  getDeliveryAddresses: PropTypes.func.isRequired,
  getOrderHistoryCount: PropTypes.func.isRequired,
  getShoppingListCount: PropTypes.func.isRequired,
  getPayments: PropTypes.func,
  upgradeProfile: PropTypes.func.isRequired,
  clearFormAction: PropTypes.func.isRequired,
  hasValidSession: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  return {
    profile: state.profile,
    profileType: state.profile.profileType,
    profileFetched: !!state.profile.profileType,
    isLoading: state.userInterface.loading[LOADING.PROFILE_UPGRADE]
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      getProfile,
      getDeliveryAddresses,
      getOrderHistoryCount,
      getShoppingListCount,
      getPayments,
      upgradeProfile,
      clearFormAction,
      hasValidSession,
      login
    },
    dispatch
  );
};

const UpgradeToFamilyCardWithCOnnect = connect(
  mapStateToProps,
  mapDispatchToProps
)(UpgradeToFamilyCard);

export default withRouter(UpgradeToFamilyCardWithCOnnect);
